"use client";

import { CmsContainedImage } from "@/components/cms/CmsContainedImage";
import Link from "next/link";
import { safeHref } from "@/lib/safe-href";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type ImageTextItem = {
  image_url?: string;
  title: string;
  body?: string;
  cta_label?: string;
  cta_url?: string;
};

export type ImageTextRowData = {
  heading?: string;
  subheading?: string;
  items?: ImageTextItem[];
};

function ImageBlock({ src, alt }: { src: string; alt: string }) {
  if (!src?.trim()) {
    return (
      <div
        className="cms-media-frame cms-block-image cms-block-image--empty aspect-[4/3] w-full max-w-full rounded-2xl border border-[var(--border)]/80 bg-gradient-to-br from-[var(--muted)] via-[var(--card)] to-[var(--section-alt)] shadow-inner"
        aria-hidden
      />
    );
  }
  return (
    <CmsContainedImage
      src={src}
      alt={alt}
      frameClassName="cms-block-image rounded-2xl border border-[var(--border)]/80 bg-[var(--muted)] shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5"
      className="sm:max-h-none"
    />
  );
}

export function ImageTextRowSection({ data }: { data: ImageTextRowData }) {
  const items = data.items ?? [];
  if (items.length === 0 && !data.heading) return null;

  return (
    <section className="cms-section-contained border-y border-[var(--border)]/70 bg-gradient-to-b from-[var(--background)] via-[var(--section-alt)]/40 to-[var(--background)] py-10 sm:py-[var(--section-space)]">
      <div className="cms-image-text-row mx-auto w-full max-w-6xl min-w-0 px-4 sm:px-5 md:px-6">
        {(data.heading || data.subheading) && (
          <header className="mx-auto mb-8 max-w-3xl text-center sm:mb-12 md:mb-16">
            {data.heading ? (
              <h2 className="text-balance text-3xl font-extrabold tracking-tight text-[var(--primary)] md:text-4xl">
                {data.heading}
              </h2>
            ) : null}
            {data.subheading ? (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-[var(--muted-foreground)] md:text-lg">
                {data.subheading}
              </p>
            ) : null}
          </header>
        )}

        <div className="cms-image-text-row__blocks flex flex-col gap-10 sm:gap-14 md:gap-24">
          {items.map((item, idx) => {
            const imageFirst = idx % 2 === 0;
            return (
              <article
                key={`${item.title}-${idx}`}
                className={cn(
                  "cms-image-text-row__item cms-section-grid grid min-w-0 grid-cols-1 items-center gap-6 sm:gap-8 md:grid-cols-2 md:gap-14 lg:gap-16",
                  !imageFirst && "md:[&>div:first-child]:order-2 md:[&>div:last-child]:order-1",
                )}
              >
                <div
                  className={cn(
                    "min-w-0 w-full max-w-full shrink-0 px-0 sm:px-1",
                    !imageFirst && "md:col-start-2",
                  )}
                >
                  <ImageBlock
                    src={item.image_url ?? ""}
                    alt={item.title?.trim() || data.heading || "Content image"}
                  />
                </div>
                <div
                  className={cn(
                    "cms-image-text-row__copy min-w-0 w-full max-w-full px-0 sm:px-1",
                    !imageFirst && "md:col-start-1 md:row-start-1",
                  )}
                >
                  {item.title?.trim() ? (
                    <h3 className="text-xl font-bold tracking-tight text-[var(--primary)] sm:text-2xl md:text-3xl">
                      {item.title}
                    </h3>
                  ) : null}
                  {item.body ? (
                    <p className="mt-3 text-pretty text-base leading-relaxed text-[var(--muted-foreground)] sm:mt-4 md:text-lg">
                      {item.body}
                    </p>
                  ) : null}
                  {item.cta_label && item.cta_url ? (
                    <Link
                      href={safeHref(item.cta_url)}
                      className={cn(
                        buttonVariants({ variant: "outline", size: "lg" }),
                        "mt-6 w-full sm:mt-8 sm:w-auto border-[var(--primary)]/25 text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white",
                      )}
                    >
                      {item.cta_label}
                    </Link>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
